import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Layers, ListTree, Search, Loader2, RefreshCw,
  Database, FileText, SlidersHorizontal, Sparkles, AlertCircle, Trash2, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { capsulesAPI, orgMemoryAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';

const DEMO_SNAPSHOT_DOMAINS = new Set(['almacenes', 'impositivo', 'lineas_produccion']);
const DEMO_PLAYBOOK_SLUGS = new Set([
  'diagnostico-inicial-incidente',
  'cuando-escalar-n1-n2',
  'consulta-parametrica-antes-de-codigo',
]);

const STATUS_BADGE = {
  draft: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  archived: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  superseded: 'bg-violet-50 text-violet-800 border-violet-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  provisional: 'bg-sky-50 text-sky-800 border-sky-200',
  merged: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
};

function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <Badge variant="outline" className={`text-[10px] rounded-sm ${STATUS_BADGE[status] || ''}`}>
      {status}
    </Badge>
  );
}

function EmptyHint({ children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 border border-dashed border-zinc-200 rounded-sm text-zinc-500">
      <AlertCircle className="w-5 h-5 text-zinc-400" />
      <p className="text-xs text-center max-w-md">{children}</p>
    </div>
  );
}

function FlagChips({ flags }) {
  if (!flags) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(flags).map(([key, on]) => (
        <Badge
          key={key}
          variant="outline"
          className={`text-[10px] rounded-sm font-mono ${on ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-zinc-50 text-zinc-500'}`}
        >
          {key}: {on ? 'on' : 'off'}
        </Badge>
      ))}
    </div>
  );
}

function isDemoSnapshot(s) {
  return Boolean(s?.is_demo) || DEMO_SNAPSHOT_DOMAINS.has(s?.domain);
}

function isDemoPlaybook(pb) {
  return Boolean(pb?.is_demo) || DEMO_PLAYBOOK_SLUGS.has(pb?.slug);
}

function DemoBadge({ show }) {
  if (!show) return null;
  return (
    <Badge variant="outline" className="text-[9px] rounded-sm bg-amber-50 text-amber-800 border-amber-200">
      bootstrap-demo
    </Badge>
  );
}

export default function OrgMemoryObservatoryPanel({ user, initialCapsuleId = '', onProbeInPlayground }) {
  const tenantId = user?.tenant_id;
  const canCurate = ['admin', 'supervisor'].includes(user?.role);
  const canBootstrap = canCurate;
  const [capsules, setCapsules] = useState([]);
  const [capsuleId, setCapsuleId] = useState(initialCapsuleId || user?.capsule_id || '');
  const [subTab, setSubTab] = useState('mapa');
  const [loading, setLoading] = useState(false);
  const [flags, setFlags] = useState(null);
  const [lenses, setLenses] = useState([]);
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventStatus, setEventStatus] = useState('');
  const [chapters, setChapters] = useState([]);
  const [indices, setIndices] = useState([]);
  const [lensFilter, setLensFilter] = useState('');
  const [proposals, setProposals] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [liveDomains, setLiveDomains] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLens, setSearchLens] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [selectedSnapshotDomain, setSelectedSnapshotDomain] = useState('');
  const [snapshotPreview, setSnapshotPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [representations, setRepresentations] = useState(null);
  const [confirmProposal, setConfirmProposal] = useState(null);
  const [confirmBootstrap, setConfirmBootstrap] = useState(false);
  const [confirmDanger, setConfirmDanger] = useState(null); // { kind, payload, title, description }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await capsulesAPI.list(tenantId);
        const list = (res.data || []).filter((c) => c?.status !== 'archived');
        if (cancelled) return;
        setCapsules(list);
        setCapsuleId((prev) => {
          if (prev && list.some((c) => c.capsule_id === prev)) return prev;
          if (initialCapsuleId && list.some((c) => c.capsule_id === initialCapsuleId)) {
            return initialCapsuleId;
          }
          return list[0]?.capsule_id || '';
        });
      } catch (err) {
        toast.error(formatApiError(err) || 'No se pudieron cargar cápsulas');
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, initialCapsuleId]);

  const loadSnapshotPreview = useCallback(async (domain) => {
    if (!tenantId || !capsuleId || !domain) return;
    setSelectedSnapshotDomain(domain);
    setPreviewLoading(true);
    try {
      const res = await orgMemoryAPI.previewSnapshot(tenantId, capsuleId, domain);
      setSnapshotPreview(res.data || null);
    } catch (err) {
      setSnapshotPreview(null);
      toast.error(formatApiError(err) || 'No se pudo cargar la vista previa');
    } finally {
      setPreviewLoading(false);
    }
  }, [tenantId, capsuleId]);

  const loadAll = useCallback(async () => {
    if (!tenantId || !capsuleId) return;
    setLoading(true);
    try {
      const [
        flagsRes,
        profileRes,
        eventsRes,
        chaptersRes,
        indicesRes,
        proposalsRes,
        snapsRes,
        catalogRes,
        playbooksRes,
        reprRes,
      ] = await Promise.all([
        orgMemoryAPI.flags(tenantId, capsuleId),
        orgMemoryAPI.getProfile(tenantId, capsuleId),
        orgMemoryAPI.listEvents(tenantId, capsuleId, eventStatus ? { status: eventStatus } : {}),
        orgMemoryAPI.listChapters(tenantId, capsuleId),
        orgMemoryAPI.listIndices(tenantId, capsuleId, lensFilter ? { lens_id: lensFilter } : {}),
        orgMemoryAPI.listProposals(tenantId, capsuleId, { status: 'pending' }),
        orgMemoryAPI.listSnapshots(tenantId, capsuleId),
        orgMemoryAPI.parametricCatalog(tenantId, capsuleId),
        orgMemoryAPI.listPlaybooks(tenantId, capsuleId),
        orgMemoryAPI.representations(tenantId, capsuleId),
      ]);
      setFlags(flagsRes.data?.flags || null);
      setLenses(flagsRes.data?.lenses || []);
      setProfile(profileRes.data?.knowledge_profile || null);
      setEvents(eventsRes.data?.events || []);
      setChapters(chaptersRes.data?.chapters || []);
      setIndices(indicesRes.data?.indices || []);
      setProposals(proposalsRes.data?.proposals || []);
      setSnapshots(snapsRes.data?.snapshots || []);
      setCatalog(catalogRes.data?.catalog || []);
      setLiveDomains(catalogRes.data?.live_domains || []);
      setPlaybooks(playbooksRes.data?.playbooks || []);
      setRepresentations(reprRes.data || null);
    } catch (err) {
      toast.error(formatApiError(err) || 'Error cargando memoria organizacional');
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsuleId, eventStatus, lensFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!snapshots.length) {
      setSelectedSnapshotDomain('');
      setSnapshotPreview(null);
      return;
    }
    if (!selectedSnapshotDomain || !snapshots.some((s) => s.domain === selectedSnapshotDomain)) {
      loadSnapshotPreview(snapshots[0].domain);
    }
  }, [snapshots, selectedSnapshotDomain, loadSnapshotPreview]);

  const counts = useMemo(() => ({
    events: events.length,
    chapters: chapters.length,
    indices: indices.reduce((n, idx) => n + (idx.entries?.length || 0), 0),
    snapshots: snapshots.length,
    playbooks: playbooks.length,
    proposals: proposals.length,
  }), [events, chapters, indices, snapshots, playbooks, proposals]);

  const runSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await orgMemoryAPI.search(tenantId, capsuleId, {
        query: searchQuery.trim(),
        lens_id: searchLens || undefined,
        top_k: 5,
      });
      setSearchResult(res.data || null);
    } catch (err) {
      toast.error(formatApiError(err) || 'Búsqueda fallida');
    } finally {
      setSearching(false);
    }
  };

  const runCurator = async () => {
    setBusy(true);
    try {
      const res = await orgMemoryAPI.runCurator(tenantId, capsuleId);
      toast.success(`Curador: ${res.data?.proposals_created ?? 0} propuestas`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo ejecutar el curador');
    } finally {
      setBusy(false);
    }
  };

  const approveProposal = async () => {
    if (!confirmProposal) return;
    setBusy(true);
    try {
      await orgMemoryAPI.approveProposal(tenantId, capsuleId, confirmProposal.proposal_id);
      toast.success('Propuesta aprobada');
      setConfirmProposal(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo aprobar');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const runDangerAction = async () => {
    if (!confirmDanger) return;
    setBusy(true);
    try {
      const { kind, payload } = confirmDanger;
      if (kind === 'archive_event') {
        await orgMemoryAPI.archiveEvent(tenantId, capsuleId, payload.event_id, {
          reason: 'curator_ui',
        });
        toast.success('Suceso archivado');
        setSelectedEvent(null);
      } else if (kind === 'delete_event') {
        await orgMemoryAPI.deleteEvent(tenantId, capsuleId, payload.event_id, {
          reason: 'curator_ui',
        });
        toast.success('Suceso eliminado');
        setSelectedEvent(null);
      } else if (kind === 'delete_snapshot') {
        await orgMemoryAPI.deleteSnapshot(tenantId, capsuleId, payload.domain);
        toast.success(`Snapshot ${payload.domain} eliminado`);
        if (selectedSnapshotDomain === payload.domain) {
          setSelectedSnapshotDomain('');
          setSnapshotPreview(null);
        }
      } else if (kind === 'delete_playbook') {
        await orgMemoryAPI.deletePlaybook(tenantId, capsuleId, payload.slug);
        toast.success(`Playbook ${payload.slug} eliminado`);
        if (selectedPlaybook?.slug === payload.slug) setSelectedPlaybook(null);
      }
      setConfirmDanger(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err) || 'Acción fallida');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const runBootstrap = async () => {
    setBusy(true);
    try {
      const res = await orgMemoryAPI.bootstrapSistema(tenantId, capsuleId);
      toast.success(res.data?.message || 'Bootstrap aplicado');
      setConfirmBootstrap(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err) || 'Bootstrap fallido');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const probePlayground = (query) => {
    if (onProbeInPlayground) {
      onProbeInPlayground(capsuleId, query);
      toast.message('Consulta enviada al Playground');
    }
  };

  const snapshotSampleRows = snapshotPreview?.snapshot?.sample_rows || [];
  const snapshotColumns = useMemo(() => {
    const keys = [];
    snapshotSampleRows.forEach((row) => {
      Object.keys(row || {}).forEach((k) => {
        if (!keys.includes(k)) keys.push(k);
      });
    });
    return keys;
  }, [snapshotSampleRows]);

  const capsuleLabel = (c) => c?.name || c?.capsule_id || '';

  return (
    <div className="space-y-4" data-testid="org-memory-observatory">
      {/* **Qué es esto, antes de mostrarlo.** Diego lo dijo el 2026-08-26: *"sigo sin entender
          exactamente dónde, por qué, para qué sirve, dónde afecta"*. Una pantalla que dice «4
          borradores» sin decir qué es un borrador, de dónde salió y qué pasa si lo apruebo, es
          una pantalla que nadie toca — y el conocimiento se queda sin aprobar para siempre. */}
      <div className="text-[11px] text-zinc-700 bg-zinc-50 border border-brand-border rounded-sm p-3 space-y-1.5"
           data-testid="que-es-la-memoria">
        <p className="font-semibold text-zinc-800">Qué es la memoria del área</p>
        <p>
          Cada vez que se <b>cierra un caso</b>, el sistema arma un resumen de qué pasaba, cuál
          era la causa y qué se hizo. Ese resumen es un <b>borrador</b>: nace sin aprobar porque
          nadie verificó todavía que sea cierto.
        </p>
        <p>
          <b>Para qué sirve.</b> Cuando llega un caso parecido, el panel <i>«Casos parecidos»</i>
          {' '}—en Casos → Memoria— muestra los aprobados: <i>«esto ya pasó, se resolvió así»</i>.
          Y el agente los recibe en su contexto. <b>Sólo los aprobados</b>: un borrador no se le
          muestra a nadie ni se le da a un agente.
        </p>
        <p>
          <b>Qué hacés vos.</b> Leer un borrador y decidir: si describe bien lo que pasó,
          <b> aprobalo</b> y pasa a estar disponible para todos. Si está mal o es irrelevante,
          descartalo. <b>Aprobar es la única forma de que el conocimiento salga de un caso y
          sirva en el siguiente.</b>
        </p>
        <p className="text-zinc-500">
          Se aprueban de a uno, desde el caso que los originó: <i>Casos → [el caso] → Memoria</i>.
          Acá abajo se ve el conjunto: cuántos hay, en qué estado, y cómo se agrupan.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3 justify-between">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-brand-primary" />
            Libro de historia / memoria organizacional
          </h2>
          <p className="text-[11px] text-zinc-500 max-w-2xl">
            Observá cómo se organiza el conocimiento: sucesos → capítulos → índices por óptica,
            más snapshots paramétricos y playbooks. La edición por ticket sigue en Tickets → Memoria.
            Guía completa: pestaña <b>Ayuda</b> de esta misma pantalla.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] text-zinc-500">Cápsula</Label>
            <select
              className="block mt-0.5 h-8 text-xs border border-zinc-200 rounded-sm px-2 bg-white min-w-[160px]"
              value={capsuleId}
              onChange={(e) => setCapsuleId(e.target.value)}
              data-testid="org-memory-capsule-select"
            >
              {capsules.map((c) => (
                <option key={c.capsule_id} value={c.capsule_id}>{capsuleLabel(c)}</option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs rounded-sm"
            onClick={loadAll}
            disabled={loading || !capsuleId}
            data-testid="org-memory-refresh"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Actualizar
          </Button>
          {canBootstrap && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs rounded-sm border-amber-300 text-amber-900"
              onClick={() => setConfirmBootstrap(true)}
              disabled={busy || !capsuleId}
              data-testid="org-memory-bootstrap"
              title="Solo datos demo; no es carga ERP real"
            >
              Zona demo: bootstrap
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2">
        <div className="p-3 bg-sky-50 border border-sky-200 rounded-sm text-[11px] text-sky-900">
          <p className="font-semibold">Tres canales distintos (no son etapas del mismo ticket)</p>
          <ul className="mt-1 text-sky-800 list-disc ml-4 space-y-0.5">
            <li><b>Suceso</b> = lección de un ticket promovido (libro de historia).</li>
            <li><b>Paramétrico</b> = tablas maestras ERP (export/carga admin).</li>
            <li><b>Playbook</b> = procedimiento SME escrito (no nace del mail).</li>
          </ul>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900">
          <p className="font-semibold">¿Por qué no aparece el ticket nuevo en Sucesos?</p>
          <p className="mt-1 text-amber-800">
            <b>No es por demora.</b> Crear un ticket no genera memoria.
            El borrador nace al <b>resolver/cerrar</b> o al pulsar <b>Extraer borrador</b> en
            Tickets → Memoria. Curá basura con Archivar / Eliminar aquí.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[
          ['Sucesos', counts.events],
          ['Capítulos', counts.chapters],
          ['Entradas índice', counts.indices],
          ['Snapshots', counts.snapshots],
          ['Playbooks', counts.playbooks],
          ['Propuestas', counts.proposals],
        ].map(([label, value]) => (
          <div key={label} className="p-2.5 border border-zinc-200 rounded-sm bg-white">
            <p className="text-[10px] text-zinc-500">{label}</p>
            <p className="text-lg font-heading font-bold text-zinc-900">{loading ? '…' : value}</p>
          </div>
        ))}
      </div>

      <Card className="border-zinc-200 rounded-sm">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
            Feature flags
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <FlagChips flags={flags} />
        </CardContent>
      </Card>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Libro de historia</p>
          <TabsList className="bg-zinc-100 rounded-sm h-auto flex flex-wrap gap-0.5 p-1 w-full justify-start">
            <TabsTrigger value="mapa" className="text-xs rounded-sm data-[state=active]:bg-white">
              <Layers className="w-3 h-3 mr-1" /> Mapa
            </TabsTrigger>
            <TabsTrigger value="sucesos" className="text-xs rounded-sm data-[state=active]:bg-white">
              Sucesos
            </TabsTrigger>
            <TabsTrigger value="capitulos" className="text-xs rounded-sm data-[state=active]:bg-white">
              Capítulos
            </TabsTrigger>
            <TabsTrigger value="indices" className="text-xs rounded-sm data-[state=active]:bg-white">
              <ListTree className="w-3 h-3 mr-1" /> Índices
            </TabsTrigger>
            <TabsTrigger value="curador" className="text-xs rounded-sm data-[state=active]:bg-white">
              <Sparkles className="w-3 h-3 mr-1" /> Curador
            </TabsTrigger>
            <TabsTrigger value="busqueda" className="text-xs rounded-sm data-[state=active]:bg-white">
              <Search className="w-3 h-3 mr-1" /> Búsqueda
            </TabsTrigger>
          </TabsList>
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-500 pt-1">Fuentes ERP / SME</p>
          <TabsList className="bg-zinc-100 rounded-sm h-auto flex flex-wrap gap-0.5 p-1 w-full justify-start">
            <TabsTrigger value="parametrico" className="text-xs rounded-sm data-[state=active]:bg-white">
              <Database className="w-3 h-3 mr-1" /> Paramétrico
            </TabsTrigger>
            <TabsTrigger value="playbooks" className="text-xs rounded-sm data-[state=active]:bg-white">
              <FileText className="w-3 h-3 mr-1" /> Playbooks
            </TabsTrigger>
            <TabsTrigger value="perfil" className="text-xs rounded-sm data-[state=active]:bg-white">
              <SlidersHorizontal className="w-3 h-3 mr-1" /> Perfil
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="mapa" className="mt-4 space-y-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-[11px] text-blue-800 space-y-2">
            <p className="font-semibold">Capas de representación</p>
            <div className="flex flex-wrap items-center gap-1 text-[10px] font-mono text-blue-700 py-1">
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Ticket</span>
              <span>→</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Suceso</span>
              <span>→</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Capítulo</span>
              <span>→</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Índice (óptica)</span>
              <span className="mx-2">|</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Parametric/*.md</span>
              <span>+</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Playbooks/*.md</span>
              <span>→</span>
              <span className="px-2 py-1 bg-white border border-blue-200 rounded-sm">Qdrant docs</span>
            </div>
            <ol className="list-decimal ml-4 space-y-1 text-blue-700">
              <li><b>Suceso</b> — resolución de un ticket (borrador → aprobado → markdown en KB). No nace del mail solo.</li>
              <li><b>Capítulo / Índice</b> — organización del libro de historia (ópticas causal, táctica…).</li>
              <li><b>Paramétrico</b> — export de maestros ERP (<span className="font-mono">Parametric/{'{domain}'}.md</span>). Canal aparte.</li>
              <li><b>Playbooks</b> — procedimientos SME escritos (<span className="font-mono">Playbooks/{'{slug}'}.md</span>). Canal aparte.</li>
              <li><b>Live habilidades</b> — datos volátiles vía API solo lectura (cuando estén habilitadas).</li>
            </ol>
            <p className="text-blue-700">
              Nada enruta solo un ticket a Paramétrico o Playbooks: son cargas distintas. El bootstrap solo sembró demos.
            </p>
          </div>
          {representations?.layers?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {representations.layers.map((layer) => (
                <div key={layer.id} className="p-2.5 border border-zinc-200 rounded-sm bg-white text-[11px]">
                  <p className="text-[10px] text-zinc-500">{layer.label}</p>
                  <p className="text-lg font-heading font-bold text-zinc-900">{layer.count}</p>
                  {layer.detail && (
                    <p className="text-[9px] text-zinc-400 font-mono mt-1 truncate" title={JSON.stringify(layer.detail)}>
                      {Object.entries(layer.detail).map(([k, v]) => `${k}:${v}`).join(' ')}
                    </p>
                  )}
                  {typeof layer.entries === 'number' && (
                    <p className="text-[9px] text-zinc-400">{layer.entries} entradas</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <Card className="border-zinc-200 rounded-sm">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Ópticas disponibles</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {(lenses || []).map((l) => (
                  <div key={l.lens_id} className="flex items-start justify-between gap-2 text-[11px]">
                    <div>
                      <p className="font-medium text-zinc-800">{l.label}</p>
                      <p className="text-zinc-500">{l.description}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono rounded-sm shrink-0">{l.lens_id}</Badge>
                  </div>
                ))}
                {!lenses?.length && <EmptyHint>Sin ópticas (flags no cargados).</EmptyHint>}
              </CardContent>
            </Card>
            <Card className="border-zinc-200 rounded-sm">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Pilares del perfil</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-1.5">
                {profile?.pillars && Object.entries(profile.pillars).map(([name, cfg]) => (
                  <div key={name} className="flex items-center justify-between text-[11px]">
                    <span className="font-mono text-zinc-700">{name}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] rounded-sm ${cfg?.enabled ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-zinc-50 text-zinc-500'}`}
                    >
                      {cfg?.enabled ? 'enabled' : 'off'}
                      {cfg?.selective ? ' · selective' : ''}
                      {cfg?.read_only ? ' · RO' : ''}
                    </Badge>
                  </div>
                ))}
                {!profile && <EmptyHint>Sin knowledge_profile en esta cápsula.</EmptyHint>}
                {profile && (
                  <p className="text-[10px] text-zinc-500 pt-2">
                    Tipo: <span className="font-mono">{profile.profile_type}</span>
                    {' · '}plantilla: <span className="font-mono">{profile.extraction_template}</span>
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sucesos" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-zinc-500">Estado</Label>
              <select
                className="h-8 text-xs border border-zinc-200 rounded-sm px-2 bg-white"
                value={eventStatus}
                onChange={(e) => setEventStatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="draft">draft</option>
                <option value="approved">approved</option>
                <option value="archived">archived</option>
                <option value="superseded">superseded</option>
              </select>
            </div>
            <p className="text-[10px] text-zinc-500 max-w-lg">
              Lista de <b>sucesos de memoria</b>, no de tickets abiertos. Un mail nuevo a Del.ia crea ticket;
              el suceso aparece al resolver/cerrar o al extraer en Tickets → Memoria.
            </p>
          </div>
          {!events.length ? (
            <EmptyHint>
              No hay sucesos aún. Resolvé/cerrá un ticket (extracción automática) o abrí el ticket →
              pestaña Memoria → Extraer borrador. Luego Actualizar en este observatorio.
            </EmptyHint>
          ) : (
            <div className="space-y-3">
            {/* **Los que esperan a alguien, arriba y con nombre.** «Hay 4 borradores» sin decir
                cuáles obliga a buscarlos, y lo que no se encuentra no se aprueba. */}
            {events.filter((e) => e.status === 'draft').length > 0 && (
              <div className="text-[11px] rounded-sm border border-amber-200 bg-amber-50/70 text-amber-900 p-2 space-y-1"
                   data-testid="borradores-esperando">
                <p>
                  <b>{events.filter((e) => e.status === 'draft').length} esperando que alguien los
                  revise.</b> Hasta que se aprueben, no se le muestran a nadie ni los recibe un
                  agente.
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {events.filter((e) => e.status === 'draft').map((e) => (
                    <Link
                      key={e.event_id}
                      to={`/tickets?ticket=${encodeURIComponent(e.source_ticket_id || '')}&tab=memory`}
                      className="text-brand-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`borrador-pendiente-${e.event_id}`}
                    >
                      {e.source_ticket_id || e.event_id}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-3">
              <div className="border border-zinc-200 rounded-sm overflow-hidden max-h-[480px] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-left text-zinc-500">
                      <th className="p-2 font-medium">Título</th>
                      <th className="p-2 font-medium">Estado</th>
                      <th className="p-2 font-medium">Capítulo</th>
                      {/* **El borrador se aprueba en su ticket, no acá.** Sin el enlace, «hay 4
                          borradores» obliga a abrir Tickets y adivinar cuáles son — y por eso no
                          se aprueba ninguno. */}
                      <th className="p-2 font-medium">Aprobar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr
                        key={ev.event_id}
                        className={`border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 ${selectedEvent?.event_id === ev.event_id ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedEvent(ev)}
                      >
                        <td className="p-2 text-zinc-800">{ev.title || ev.event_id}</td>
                        <td className="p-2"><StatusBadge status={ev.status} /></td>
                        <td className="p-2 font-mono text-zinc-500">{ev.chapter_id ? `${ev.chapter_id.slice(0, 8)}…` : '—'}</td>
                        <td className="p-2">
                          {ev.source_ticket_id ? (
                            <Link
                              to={`/tickets?ticket=${encodeURIComponent(ev.source_ticket_id)}&tab=memory`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`ir-a-aprobar-${ev.event_id}`}
                              className="text-brand-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                              title={ev.status === 'draft'
                                ? `Abrir el ticket ${ev.source_ticket_id} para revisarlo y aprobarlo`
                                : `Abrir el ticket ${ev.source_ticket_id}`}
                            >
                              {ev.status === 'draft' ? 'revisar' : 'ver'}
                              <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                          ) : <span className="text-zinc-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Detalle</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 text-[11px] space-y-2">
                  {!selectedEvent ? (
                    <p className="text-zinc-500">Seleccioná un suceso.</p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusBadge status={selectedEvent.status} />
                        <Badge variant="outline" className="text-[10px] rounded-sm">{selectedEvent.share_scope}</Badge>
                        {typeof selectedEvent.confidence === 'number' && (
                          <Badge variant="outline" className="text-[10px] rounded-sm">
                            conf {(selectedEvent.confidence * 100).toFixed(0)}%
                          </Badge>
                        )}
                      </div>
                      <p>
                        <span className="text-zinc-500">Ticket:</span>{' '}
                        {selectedEvent.source_ticket_id ? (
                          <Link
                            to={`/tickets?ticket=${encodeURIComponent(selectedEvent.source_ticket_id)}&tab=memory`}
                            data-testid="ir-al-ticket-del-suceso"
                            className="font-mono text-brand-primary hover:underline inline-flex items-center gap-1"
                          >
                            {selectedEvent.source_ticket_id}
                            <ExternalLink className="w-2.5 h-2.5" />
                          </Link>
                        ) : <span className="font-mono">—</span>}
                        {selectedEvent.status === 'draft' && (
                          <span className="text-amber-700"> · se aprueba ahí</span>
                        )}
                      </p>
                      <p><span className="text-zinc-500">Síntomas:</span> {selectedEvent.symptoms || '—'}</p>
                      <p><span className="text-zinc-500">Causa:</span> {selectedEvent.root_cause || '—'}</p>
                      <div>
                        <p className="text-zinc-500 mb-1">Pasos</p>
                        <ul className="list-disc ml-4 space-y-0.5">
                          {(selectedEvent.solution_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                      <div>
                        <p className="text-zinc-500 mb-1">Lens tags</p>
                        <pre className="p-2 bg-zinc-900 text-zinc-100 rounded-sm text-[10px] overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(selectedEvent.lens_tags || {}, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <p className="text-zinc-500 mb-1">Uso</p>
                        <pre className="p-2 bg-zinc-50 border border-zinc-100 rounded-sm text-[10px] overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(selectedEvent.usage_stats || {}, null, 2)}
                        </pre>
                      </div>
                      {canCurate && selectedEvent.status !== 'archived' && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] rounded-sm"
                            disabled={busy}
                            onClick={() => setConfirmDanger({
                              kind: 'archive_event',
                              payload: { event_id: selectedEvent.event_id },
                              title: 'Archivar suceso',
                              description: 'Deja de recuperarse e inyectarse. Se purgan vectores Qdrant si estaba aprobado.',
                            })}
                          >
                            Archivar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] rounded-sm text-red-700 border-red-200"
                            disabled={busy}
                            onClick={() => setConfirmDanger({
                              kind: 'delete_event',
                              payload: { event_id: selectedEvent.event_id },
                              title: 'Eliminar suceso',
                              description: 'Borrado permanente de Mongo y Qdrant. Usá esto para borradores o basura curatorial.',
                            })}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="capitulos" className="mt-4 space-y-3">
          {!chapters.length ? (
            <EmptyHint>Sin capítulos. Aparecen al promover sucesos o al correr el curador.</EmptyHint>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {chapters.map((ch) => (
                <Card key={ch.chapter_id} className="border-zinc-200 rounded-sm">
                  <CardHeader className="py-3 px-4 flex flex-row items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-xs font-semibold text-zinc-900">{ch.title || ch.chapter_id}</CardTitle>
                      <p className="text-[10px] font-mono text-zinc-400 mt-0.5">{ch.chapter_id}</p>
                    </div>
                    <StatusBadge status={ch.status} />
                  </CardHeader>
                  <CardContent className="px-4 pb-4 text-[11px] space-y-2">
                    <p className="text-zinc-600">{ch.summary || 'Sin resumen'}</p>
                    <p className="text-zinc-500">
                      {ch.event_ids?.length || 0} sucesos
                      {typeof ch.cohesion_score === 'number' && ` · cohesión ${(ch.cohesion_score * 100).toFixed(0)}%`}
                    </p>
                    {(ch.event_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {ch.event_ids.slice(0, 8).map((id) => (
                          <Badge key={id} variant="outline" className="text-[9px] font-mono rounded-sm">{id.slice(0, 8)}</Badge>
                        ))}
                        {ch.event_ids.length > 8 && (
                          <Badge variant="outline" className="text-[9px] rounded-sm">+{ch.event_ids.length - 8}</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="indices" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-[10px] text-zinc-500">Óptica</Label>
            <select
              className="h-8 text-xs border border-zinc-200 rounded-sm px-2 bg-white"
              value={lensFilter}
              onChange={(e) => setLensFilter(e.target.value)}
            >
              <option value="">Todas</option>
              {(lenses || []).filter((l) => !l.tenant_level).map((l) => (
                <option key={l.lens_id} value={l.lens_id}>{l.label}</option>
              ))}
            </select>
          </div>
          {!indices.length ? (
            <EmptyHint>Sin índices. Se construyen al aprobar sucesos (curador / placement).</EmptyHint>
          ) : (
            <div className="space-y-3">
              {indices.map((idx) => (
                <Card key={idx.index_id || idx.lens_id} className="border-zinc-200 rounded-sm">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-xs font-semibold text-zinc-900">
                        Índice · {idx.lens_id}
                      </CardTitle>
                      <Badge variant="outline" className="text-[10px] rounded-sm">
                        {idx.entries?.length || 0} entradas
                      </Badge>
                    </div>
                    {idx.updated_at && (
                      <p className="text-[10px] text-zinc-400 mt-1">Actualizado {idx.updated_at}</p>
                    )}
                  </CardHeader>
                  <CardContent className="px-4 pb-4">
                    {!(idx.entries || []).length ? (
                      <p className="text-[11px] text-zinc-500">Sin entradas.</p>
                    ) : (
                      <div className="border border-zinc-100 rounded-sm overflow-hidden">
                        <table className="w-full text-[11px]">
                          <thead className="bg-zinc-50">
                            <tr className="text-left text-zinc-500">
                              <th className="p-2 font-medium">Título</th>
                              <th className="p-2 font-medium">Resumen routing</th>
                              <th className="p-2 font-medium">Eventos</th>
                              <th className="p-2 font-medium">Capítulos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {idx.entries.map((en) => (
                              <tr key={en.entry_id} className="border-t border-zinc-100 align-top">
                                <td className="p-2 font-medium text-zinc-800">{en.title}</td>
                                <td className="p-2 text-zinc-600">{en.routing_summary}</td>
                                <td className="p-2">{en.event_count ?? '—'}</td>
                                <td className="p-2 font-mono text-[10px] text-zinc-500">
                                  {(en.chapter_ids || []).map((id) => id.slice(0, 8)).join(', ') || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="parametrico" className="mt-4 space-y-4">
          <div className="grid lg:grid-cols-3 gap-3">
            <Card className="border-zinc-200 rounded-sm lg:col-span-1">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                  Snapshots materializados
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {!snapshots.length ? (
                  <EmptyHint>Sin snapshots. Usá Bootstrap piloto o POST parametric/snapshots.</EmptyHint>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto">
                    {snapshots.map((s) => (
                      <button
                        key={s.snapshot_id || s.domain}
                        type="button"
                        onClick={() => loadSnapshotPreview(s.domain)}
                        className={`w-full text-left p-2.5 border rounded-sm text-[11px] hover:bg-zinc-50 ${
                          selectedSnapshotDomain === s.domain ? 'border-blue-300 bg-blue-50' : 'border-zinc-100'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-zinc-900">{s.label || s.domain}</p>
                          <div className="flex items-center gap-1 shrink-0">
                            <DemoBadge show={isDemoSnapshot(s)} />
                            <Badge variant="outline" className="text-[10px] font-mono rounded-sm">{s.domain}</Badge>
                          </div>
                        </div>
                        <p className="text-zinc-500 mt-1">
                          {s.row_count ?? 0} filas · <span className="font-mono">{s.file_path}</span>
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1 items-center">
                          <Badge
                            variant="outline"
                            className={`text-[9px] rounded-sm ${
                              (s.vector_chunks ?? 0) > 0
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                : 'bg-amber-50 text-amber-800 border-amber-200'
                            }`}
                          >
                            Qdrant: {s.vector_chunks ?? 0} chunks
                          </Badge>
                          <Badge variant="outline" className="text-[9px] rounded-sm">
                            as_of {s.as_of ? String(s.as_of).slice(0, 10) : '—'}
                          </Badge>
                          {canCurate && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-1.5 text-[9px] text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDanger({
                                  kind: 'delete_snapshot',
                                  payload: { domain: s.domain },
                                  title: `Eliminar snapshot ${s.domain}`,
                                  description: 'Borra Mongo + chunks Qdrant de Parametric/. Acción irreversible.',
                                });
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-0.5" /> Eliminar
                            </Button>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-zinc-200 rounded-sm lg:col-span-2">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                  Vista previa · {selectedSnapshotDomain || '—'}
                </CardTitle>
                {selectedSnapshotDomain && onProbeInPlayground && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] rounded-sm"
                    onClick={() => probePlayground(`${selectedSnapshotDomain} configuración ERP`)}
                  >
                    Probar en Playground
                  </Button>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {previewLoading ? (
                  <div className="flex items-center gap-2 text-xs text-zinc-500 py-8 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando vista previa…
                  </div>
                ) : !snapshotPreview ? (
                  <EmptyHint>Seleccioná un snapshot para ver filas, markdown y estado en Qdrant.</EmptyHint>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge
                        variant="outline"
                        className={`text-[10px] rounded-sm ${
                          (snapshotPreview.vector_chunks ?? 0) > 0
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}
                      >
                        {snapshotPreview.vector_chunks ?? 0} chunks en Qdrant (colección docs)
                      </Badge>
                      {(snapshotPreview.snapshot?.row_count ?? 0) > snapshotSampleRows.length && (
                        <Badge variant="outline" className="text-[10px] rounded-sm">
                          Muestra {snapshotSampleRows.length} de {snapshotPreview.snapshot.row_count} filas
                        </Badge>
                      )}
                    </div>
                    {snapshotSampleRows.length > 0 ? (
                      <div className="border border-zinc-100 rounded-sm overflow-auto max-h-40">
                        <table className="w-full text-[10px]">
                          <thead className="bg-zinc-50 sticky top-0">
                            <tr>
                              {snapshotColumns.map((col) => (
                                <th key={col} className="p-1.5 text-left font-medium text-zinc-500">{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {snapshotSampleRows.map((row, i) => (
                              <tr key={i} className="border-t border-zinc-100">
                                {snapshotColumns.map((col) => (
                                  <td key={col} className="p-1.5 text-zinc-700">{String(row[col] ?? '')}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 p-2 rounded-sm">
                        Sin filas de muestra en Mongo (snapshot anterior al bootstrap actual).
                        Re-ejecutá Bootstrap piloto o volvé a cargar el snapshot para persistir sample_rows.
                      </p>
                    )}
                    <div>
                      <p className="text-[10px] text-zinc-500 mb-1">Markdown indexado</p>
                      <pre className="p-2 bg-zinc-900 text-zinc-100 rounded-sm text-[10px] overflow-auto max-h-36 whitespace-pre-wrap">
                        {snapshotPreview.markdown || ''}
                      </pre>
                    </div>
                    {(snapshotPreview.chunk_samples || []).length > 0 && (
                      <div>
                        <p className="text-[10px] text-zinc-500 mb-1">Fragmentos en vector store</p>
                        <div className="space-y-1">
                          {snapshotPreview.chunk_samples.map((ch, i) => (
                            <div key={i} className="p-2 bg-zinc-50 border border-zinc-100 rounded-sm text-[10px]">
                              <span className="font-mono text-zinc-500">
                                chunk {ch.chunk_index} · {ch.source || ch.kb_kind || 'docs'}
                              </span>
                              <p className="text-zinc-700 mt-0.5 line-clamp-3">
                                {ch.chunk_content || ch.content_preview || '—'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                    Catálogo de dominios estables
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  {catalog.map((d) => {
                    const seeded = snapshots.some((s) => s.domain === d.domain);
                    return (
                      <div key={d.domain} className="flex items-start justify-between gap-2 text-[11px]">
                        <div>
                          <p className="font-medium text-zinc-800">{d.label}</p>
                          <p className="text-zinc-500 font-mono text-[10px]">{d.domain} · {d.stability}</p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-[10px] rounded-sm shrink-0 ${seeded ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : ''}`}
                        >
                          {seeded ? 'en KB' : 'pendiente'}
                        </Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                    Dominios live (skill)
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  {liveDomains.map((d) => (
                    <div key={d.domain} className="text-[11px]">
                      <p className="font-medium text-zinc-800">{d.label}</p>
                      <p className="text-zinc-500 font-mono text-[10px]">
                        {d.domain} · skill {d.skill_hint} · {d.stability}
                      </p>
                    </div>
                  ))}
                  {!liveDomains.length && <p className="text-[11px] text-zinc-500">Sin dominios live.</p>}
                </CardContent>
              </Card>
            </div>
        </TabsContent>

        <TabsContent value="playbooks" className="mt-4 space-y-3">
          {!playbooks.length ? (
            <EmptyHint>Sin playbooks. El bootstrap Sistema siembra tres procedimientos SME.</EmptyHint>
          ) : (
            <div className="grid lg:grid-cols-2 gap-3">
              <div className="space-y-2">
                {playbooks.map((pb) => (
                  <button
                    key={pb.playbook_id || pb.slug}
                    type="button"
                    onClick={() => setSelectedPlaybook(pb)}
                    className={`w-full text-left p-2.5 border rounded-sm text-[11px] hover:bg-zinc-50 ${
                      selectedPlaybook?.slug === pb.slug ? 'border-blue-300 bg-blue-50' : 'border-zinc-200'
                    }`}
                  >
                    <p className="font-medium text-zinc-900">{pb.title}</p>
                    <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{pb.file_path || pb.slug}</p>
                    <div className="flex flex-wrap gap-1 mt-1 items-center">
                      <DemoBadge show={isDemoPlaybook(pb)} />
                      <Badge
                        variant="outline"
                        className={`text-[9px] rounded-sm ${
                          (pb.vector_chunks ?? 0) > 0
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}
                      >
                        Qdrant: {pb.vector_chunks ?? 0} chunks
                      </Badge>
                      {canCurate && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-[9px] text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDanger({
                              kind: 'delete_playbook',
                              payload: { slug: pb.slug },
                              title: `Eliminar playbook ${pb.slug}`,
                              description: 'Borra Mongo + chunks Qdrant de Playbooks/. Acción irreversible.',
                            });
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-0.5" /> Eliminar
                        </Button>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Contenido</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  {!selectedPlaybook ? (
                    <p className="text-[11px] text-zinc-500">Seleccioná un playbook.</p>
                  ) : (
                    <pre className="p-3 bg-zinc-50 border border-zinc-100 rounded-sm text-[11px] whitespace-pre-wrap overflow-auto max-h-[420px]">
                      {selectedPlaybook.body_markdown || '(vacío)'}
                    </pre>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="curador" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-8 text-xs rounded-sm"
              onClick={runCurator}
              disabled={busy || !capsuleId}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Ejecutar curador
            </Button>
            <p className="text-[10px] text-zinc-500">Genera propuestas de colocación / merge / revisión.</p>
          </div>
          {!proposals.length ? (
            <EmptyHint>Sin propuestas pendientes.</EmptyHint>
          ) : (
            <div className="space-y-2">
              {proposals.map((p) => (
                <div key={p.proposal_id} className="p-3 border border-zinc-200 rounded-sm text-[11px] space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-mono rounded-sm">{p.kind}</Badge>
                      <StatusBadge status={p.status} />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] rounded-sm"
                      onClick={() => setConfirmProposal(p)}
                    >
                      Aprobar
                    </Button>
                  </div>
                  <pre className="p-2 bg-zinc-50 rounded-sm text-[10px] overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(p.detail || {}, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="busqueda" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] text-zinc-500">Consulta (como el agente)</Label>
              <Input
                className="h-8 text-xs rounded-sm mt-0.5"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ej. IVA almacén planta norte"
                data-testid="org-memory-search-query"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Óptica</Label>
              <select
                className="block mt-0.5 h-8 text-xs border border-zinc-200 rounded-sm px-2 bg-white"
                value={searchLens}
                onChange={(e) => setSearchLens(e.target.value)}
              >
                <option value="">auto</option>
                {(lenses || []).filter((l) => !l.tenant_level).map((l) => (
                  <option key={l.lens_id} value={l.lens_id}>{l.label}</option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              className="h-8 text-xs rounded-sm"
              onClick={runSearch}
              disabled={searching || !searchQuery.trim()}
            >
              {searching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              Buscar
            </Button>
          </div>
          {searchResult && (
            <div className="space-y-3">
              <p className="text-[11px] text-zinc-600">
                Óptica usada: <span className="font-mono">{searchResult.lens_id || '—'}</span>
              </p>
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                    Entradas de índice rankeadas
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <pre className="p-2 bg-zinc-50 rounded-sm text-[10px] overflow-auto max-h-48 whitespace-pre-wrap">
                    {JSON.stringify(searchResult.index_entries || [], null, 2)}
                  </pre>
                </CardContent>
              </Card>
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                    Sucesos recuperados
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  {(searchResult.events || []).length === 0 ? (
                    <p className="text-[11px] text-zinc-500">Ningún suceso.</p>
                  ) : (
                    (searchResult.events || []).map((ev) => (
                      <div key={ev.event_id} className="p-2 border border-zinc-100 rounded-sm text-[11px]">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={ev.status} />
                          <span className="font-medium">{ev.title}</span>
                        </div>
                        <p className="text-zinc-500 mt-1 line-clamp-2">{ev.symptoms}</p>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="perfil" className="mt-4 space-y-3">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
                knowledge_profile (solo lectura)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <pre className="p-3 bg-zinc-900 text-zinc-100 rounded-sm text-[10px] overflow-auto max-h-[480px] whitespace-pre-wrap">
                {JSON.stringify(profile || {}, null, 2)}
              </pre>
              <p className="text-[10px] text-zinc-500 mt-2">
                Dominios paramétricos del perfil: {(profile?.parametric_domains || []).join(', ') || '—'}
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmActionDialog
        open={!!confirmProposal}
        onOpenChange={(open) => { if (!open) setConfirmProposal(null); }}
        title="Aprobar propuesta del curador"
        description={
          confirmProposal
            ? `¿Aprobar propuesta ${confirmProposal.kind} (${confirmProposal.proposal_id})? Esta acción reorganiza capítulos/índices.`
            : ''
        }
        confirmLabel="Aprobar"
        onConfirm={approveProposal}
      />

      <ConfirmActionDialog
        open={confirmBootstrap}
        onOpenChange={setConfirmBootstrap}
        title="Zona demo — Bootstrap piloto Sistema"
        description="Siembra knowledge_profile técnico, 3 playbooks SME DEMO y snapshots DEMO (almacenes, impositivo, líneas). NO es carga ERP real. Después borrá los demos con Eliminar en Paramétrico/Playbooks."
        confirmText="Ejecutar bootstrap demo"
        onConfirm={runBootstrap}
      />

      <ConfirmActionDialog
        open={!!confirmDanger}
        onOpenChange={(open) => { if (!open) setConfirmDanger(null); }}
        title={confirmDanger?.title || 'Confirmar'}
        description={confirmDanger?.description || ''}
        confirmText="Confirmar"
        onConfirm={runDangerAction}
      />
    </div>
  );
}
